/* Zarpreet — theme behaviours. No dependencies. Progressive enhancement only:
   every component is usable (scrollable / linked) with JS disabled. */
(function () {
  'use strict';

  // Sections each request this file; run the setup only once.
  if (window.__zarpreetLoaded) return;
  window.__zarpreetLoaded = true;

  /* ------------------------------------------------ hero slideshow */
  class ZpHero extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      if (!this.track || this.slides.length < 2) return;

      this.index = 0;
      this.interval = parseInt(this.dataset.interval || '0', 10);

      this.dots.forEach((dot, i) =>
        dot.addEventListener('click', () => this.goTo(i, true))
      );

      this.track.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());

      // Hold the auto-advance until the page has finished loading. Slides after
      // the first are lazy, so they only start downloading once the carousel
      // scrolls to them. On a slow connection the timer would otherwise fire
      // mid-paint and pull slide two down in direct competition with the LCP
      // image. Waiting for `load` keeps the first paint's bandwidth to itself.
      if (document.readyState === 'complete') {
        this.start();
      } else {
        window.addEventListener('load', () => this.start(), { once: true });
      }
    }

    disconnectedCallback() { this.stop(); }

    onScroll() {
      clearTimeout(this._t);
      this._t = setTimeout(() => {
        const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
        this.setActive(i);
      }, 90);
    }

    goTo(i, userInitiated) {
      this.track.scrollTo({
        left: this.track.clientWidth * i,
        behavior: userInitiated ? 'smooth' : 'auto'
      });
      this.setActive(i);
      if (userInitiated) this.restart();
    }

    setActive(i) {
      this.index = i;
      this.dots.forEach((d, n) => d.setAttribute('aria-selected', String(n === i)));
    }

    start() {
      if (!this.interval) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.stop();
      this._timer = setInterval(() => {
        this.goTo((this.index + 1) % this.slides.length, true);
      }, this.interval);
    }

    stop() { clearInterval(this._timer); }
    restart() { this.stop(); this.start(); }
  }

  /* ------------------------------------------------- product rail */
  class ZpRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      if (!this.track) return;

      const step = () => this.track.clientWidth * 0.8;
      if (this.prev) this.prev.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: 'smooth' }));
      if (this.next) this.next.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: 'smooth' }));

      this.track.addEventListener('scroll', this.sync.bind(this), { passive: true });
      window.addEventListener('resize', this.sync.bind(this));
      this.sync();
    }

    sync() {
      if (!this.prev || !this.next) return;
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      this.prev.disabled = this.track.scrollLeft <= 2;
      this.next.disabled = this.track.scrollLeft >= max;
    }
  }

  /* ---------------------------------------------- product gallery */
  class ZpGallery extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      this.stage = this.querySelector('[data-stage]');
      this.scroller = this.querySelector('[data-thumb-scroll]');

      this.thumbs.forEach((btn, i) =>
        btn.addEventListener('click', () => this.select(i))
      );

      this.querySelectorAll('[data-thumb-arrow]').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (!this.scroller) return;
          const dir = btn.dataset.thumbArrow === 'next' ? 1 : -1;
          this.scroller.scrollBy({ top: dir * this.scroller.clientHeight * 0.6, behavior: 'smooth' });
        });
      });

      const zoomBtn = this.querySelector('[data-zoom]');
      if (zoomBtn && this.stage) {
        zoomBtn.addEventListener('click', () => this.stage.classList.toggle('is-zoomed'));
        this.stage.addEventListener('mousemove', (e) => {
          if (!this.stage.classList.contains('is-zoomed')) return;
          const r = this.stage.getBoundingClientRect();
          this.stage.style.setProperty('--zp-zoom-x', ((e.clientX - r.left) / r.width) * 100 + '%');
          this.stage.style.setProperty('--zp-zoom-y', ((e.clientY - r.top) / r.height) * 100 + '%');
        });
        this.stage.addEventListener('mouseleave', () => this.stage.classList.remove('is-zoomed'));
      }

      this.dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.select(i)));

      this.index = 0;
      this.bindSwipe();

      // Variant changes broadcast a media id to jump the gallery.
      document.addEventListener('zp:variant', (e) => {
        if (!e.detail || !e.detail.mediaId) return;
        const i = this.slides.findIndex((s) => s.dataset.mediaId === String(e.detail.mediaId));
        if (i > -1) this.select(i);
      });
    }

    select(i) {
      if (i < 0 || i >= this.slides.length) return;
      this.index = i;
      this.slides.forEach((s, n) => s.setAttribute('data-active', String(n === i)));
      this.thumbs.forEach((t, n) => t.setAttribute('aria-selected', String(n === i)));
      this.dots.forEach((d, n) => d.setAttribute('aria-selected', String(n === i)));
      this.scrollThumbIntoView(i);
    }

    // Keep the active thumbnail visible in the rail — vertical on desktop,
    // horizontal on phones. scrollIntoView handles both axes.
    scrollThumbIntoView(i) {
      const thumb = this.thumbs[i];
      if (!thumb || !this.scroller) return;
      if (typeof thumb.scrollIntoView !== 'function') return;
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      thumb.scrollIntoView({
        behavior: smooth ? 'smooth' : 'auto',
        block: 'nearest',
        inline: 'nearest'
      });
    }

    // Horizontal swipe on the main image. Ignores mostly-vertical drags so it
    // never fights page scrolling.
    bindSwipe() {
      if (!this.stage) return;
      let x0 = null;
      let y0 = null;

      this.stage.addEventListener('touchstart', (e) => {
        if (e.touches.length !== 1) return;
        x0 = e.touches[0].clientX;
        y0 = e.touches[0].clientY;
      }, { passive: true });

      this.stage.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        const dy = e.changedTouches[0].clientY - y0;
        x0 = null;
        if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.4) return;
        const next = dx < 0 ? this.index + 1 : this.index - 1;
        this.select(Math.max(0, Math.min(this.slides.length - 1, next)));
      }, { passive: true });
    }
  }

  /* ------------------------------------------------- mobile menu drawer */
  class ZpMenu extends HTMLElement {
    connectedCallback() {
      this.nav = this.querySelector('.zp-nav');
      this.scrim = this.querySelector('.zp-nav__scrim');
      this.opener = this.querySelector('[data-menu-open]');
      if (!this.nav || !this.opener) return;

      this.opener.addEventListener('click', () => this.open());
      this.querySelectorAll('[data-menu-close]').forEach((el) =>
        el.addEventListener('click', () => this.close())
      );

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
      });

      // A link tap closes the drawer before the new page paints.
      this.nav.addEventListener('click', (e) => {
        if (e.target.closest('a')) this.close();
      });

      // Returning to desktop width mid-session should never leave a stuck drawer.
      this.mq = window.matchMedia('(min-width: 990px)');
      const onChange = () => { if (this.mq.matches) this.close(); };
      if (this.mq.addEventListener) this.mq.addEventListener('change', onChange);
      else this.mq.addListener(onChange);
    }

    open() {
      this.classList.add('is-open');
      this.opener.setAttribute('aria-expanded', 'true');
      if (this.scrim) this.scrim.hidden = false;
      document.body.style.overflow = 'hidden';
      const first = this.nav.querySelector('a, button');
      if (first) first.focus({ preventScroll: true });
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.opener.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      if (this.scrim) {
        // Let the fade finish before removing it from the layer stack.
        setTimeout(() => { this.scrim.hidden = true; }, 320);
      }
    }
  }

  /* ------------------------------------------------ quantity input */
  class ZpQty extends HTMLElement {
    connectedCallback() {
      const input = this.querySelector('input');
      if (!input) return;
      this.querySelectorAll('button').forEach((btn) => {
        btn.addEventListener('click', () => {
          const dir = btn.dataset.dir === 'up' ? 1 : -1;
          const min = parseInt(input.min || '1', 10);
          const next = (parseInt(input.value, 10) || min) + dir;
          input.value = String(Math.max(min, next));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  }

  /* --------------------------------------------- variant selection */
  class ZpVariants extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form');
      try {
        this.variants = JSON.parse(this.querySelector('[type="application/json"]').textContent);
      } catch (e) {
        return;
      }
      this.idInput = this.form && this.form.querySelector('[name="id"]');
      this.addEventListener('change', this.onChange.bind(this));
    }

    onChange() {
      const chosen = Array.from(this.querySelectorAll('input[type="radio"]:checked')).map((i) => i.value);
      const match = this.variants.find((v) =>
        v.options.every((opt, i) => opt === chosen[i])
      );
      const btn = this.form && this.form.querySelector('[data-atc]');

      if (!match) {
        if (btn) { btn.disabled = true; btn.textContent = btn.dataset.unavailable || 'Unavailable'; }
        return;
      }
      if (this.idInput) this.idInput.value = match.id;
      if (btn) {
        btn.disabled = !match.available;
        btn.textContent = match.available ? (btn.dataset.available || 'Add to bag') : (btn.dataset.soldOut || 'Sold out');
      }

      const priceEl = document.querySelector('[data-price]');
      if (priceEl && match.price_html) priceEl.innerHTML = match.price_html;

      const skuEl = document.querySelector('[data-sku]');
      if (skuEl && match.sku) skuEl.textContent = skuEl.dataset.prefix + ' ' + match.sku;

      if (window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', match.id);
        window.history.replaceState({}, '', url);
      }

      document.dispatchEvent(new CustomEvent('zp:variant', { detail: { mediaId: match.featured_media_id } }));
    }
  }

  /* -------------------------------------------- wishlist (local UI) */
  function wishlist() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wish]');
      if (!btn) return;
      e.preventDefault();
      const on = btn.getAttribute('aria-pressed') === 'true';
      btn.setAttribute('aria-pressed', String(!on));
      btn.setAttribute('aria-label', (on ? 'Save' : 'Remove') + ' this piece');
    });
  }

  /* -------------------------------------------------- back to top */
  function backToTop() {
    const btn = document.querySelector('[data-back-to-top]');
    if (!btn) return;
    btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
    const toggle = () => btn.classList.toggle('is-visible', window.scrollY > 700);
    window.addEventListener('scroll', toggle, { passive: true });
    toggle();
  }

  const define = (name, ctor) => { if (!customElements.get(name)) customElements.define(name, ctor); };
  define('zp-hero', ZpHero);
  define('zp-rail', ZpRail);
  define('zp-gallery', ZpGallery);
  define('zp-qty', ZpQty);
  define('zp-variants', ZpVariants);
  define('zp-menu', ZpMenu);

  wishlist();
  document.addEventListener('DOMContentLoaded', backToTop);
})();
